import { useEffect, useMemo, useState } from 'react';
import {
  DuplicateDonor,
  DuplicateGroup,
  DuplicateIndividual,
  MergeResult,
  mergeDonors,
  mergeIndividuals,
} from '../services/duplicateService';

type Kind = 'client' | 'donor';

type Props =
  | {
      kind: 'client';
      group: DuplicateGroup<DuplicateIndividual>;
      onClose: () => void;
      onComplete: (result: MergeResult) => void;
    }
  | {
      kind: 'donor';
      group: DuplicateGroup<DuplicateDonor>;
      onClose: () => void;
      onComplete: (result: MergeResult) => void;
    };

interface FieldDef {
  key: string;        // camelCase JSON name sent in fieldChoices
  label: string;
  format?: (v: unknown) => string;
}

const CLIENT_FIELDS: FieldDef[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'dateOfBirth', label: 'Date of birth', format: (v) => (typeof v === 'string' ? v.slice(0, 10) : '—') },
  { key: 'gender', label: 'Gender' },
  { key: 'raceEthnicity', label: 'Race / Ethnicity' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'zipCode', label: 'ZIP code' },
  { key: 'phoneNumber', label: 'Phone number' },
  { key: 'emailAddress', label: 'Email' },
  { key: 'clientType', label: 'Client type' },
];

const DONOR_FIELDS: FieldDef[] = [
  { key: 'fullName', label: 'Full name' },
  { key: 'salutation', label: 'Salutation' },
  { key: 'organizationCompany', label: 'Organization' },
  { key: 'emailAddress', label: 'Email' },
  { key: 'phoneNumber', label: 'Phone number' },
  { key: 'mailingAddress', label: 'Mailing address' },
  { key: 'donorType', label: 'Donor type' },
  { key: 'priorityLevel', label: 'Priority level' },
  { key: 'preferredRecognition', label: 'Recognition' },
];

function fmt(value: unknown, formatter?: (v: unknown) => string): string {
  if (formatter) {return formatter(value) || '—';}
  if (value === null || value === undefined || value === '') {return '—';}
  return String(value);
}

function recordId(kind: Kind, member: DuplicateIndividual | DuplicateDonor): number {
  return kind === 'client'
    ? (member as DuplicateIndividual).individualID
    : (member as DuplicateDonor).donorID;
}

function recordLabel(kind: Kind, member: DuplicateIndividual | DuplicateDonor): string {
  if (kind === 'client') {
    const m = member as DuplicateIndividual;
    return `${m.clientID || `#${m.individualID}`} · ${m.firstName || ''} ${m.lastName || ''}`.trim();
  }
  const d = member as DuplicateDonor;
  return `#${d.donorID} · ${d.fullName || '(no name)'}`;
}

export function MergeDialog({ kind, group, onClose, onComplete }: Props) {
  const fields = kind === 'client' ? CLIENT_FIELDS : DONOR_FIELDS;
  const members = group.members;

  // Default: first member is winner, second is loser
  const [winnerId, setWinnerId] = useState<number>(recordId(kind, members[0]));
  const [loserId, setLoserId] = useState<number>(
    members.length > 1 ? recordId(kind, members[1]) : recordId(kind, members[0])
  );

  const winner = members.find((m) => recordId(kind, m) === winnerId)!;
  const loser = members.find((m) => recordId(kind, m) === loserId)!;

  const [choices, setChoices] = useState<Record<string, 'winner' | 'loser'>>({});
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset choices and confirm text whenever winner/loser changes
  useEffect(() => {
    setChoices({});
    setConfirmText('');
    setError('');
  }, [winnerId, loserId]);

  const expectedConfirm = useMemo(() => {
    if (kind === 'client') {
      return (loser as DuplicateIndividual).clientID || `#${(loser as DuplicateIndividual).individualID}`;
    }
    return `#${(loser as DuplicateDonor).donorID}`;
  }, [kind, loser]);

  // For manual merges, the duplicate scanner did not flag this pair. Show a
  // strong warning when the records look unrelated (different last names for
  // clients, different full names for donors) so the admin gets one more
  // sanity check before confirming.
  const isManualMerge = group.matchedRules.some((r) => r.name === 'manual');
  const namesLookDifferent = useMemo(() => {
    if (kind === 'client') {
      const wl = ((winner as DuplicateIndividual).lastName || '').trim().toLowerCase();
      const ll = ((loser as DuplicateIndividual).lastName || '').trim().toLowerCase();
      return wl && ll && wl !== ll;
    }
    const wn = ((winner as DuplicateDonor).fullName || '').trim().toLowerCase();
    const ln = ((loser as DuplicateDonor).fullName || '').trim().toLowerCase();
    return wn && ln && wn !== ln;
  }, [kind, winner, loser]);

  const conflictingFields = fields.filter((f) => {
    const w = (winner as unknown as Record<string, unknown>)[f.key];
    const l = (loser as unknown as Record<string, unknown>)[f.key];
    return (w ?? null) !== (l ?? null);
  });

  const submit = async () => {
    if (winnerId === loserId) {
      setError('Winner and loser must be different records.');
      return;
    }
    if (confirmText.trim() !== expectedConfirm) {
      setError(`Type "${expectedConfirm}" exactly to confirm.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result =
        kind === 'client'
          ? await mergeIndividuals(winnerId, loserId, choices)
          : await mergeDonors(winnerId, loserId, choices);
      onComplete(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Merge failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Merge records"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        className="card"
        style={{
          background: 'white',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ marginTop: 0 }}>
          Merge {kind === 'client' ? 'clients' : 'donors'}
        </h2>
        <p style={{ color: '#666' }}>
          The <strong>winner</strong> survives. The <strong>loser</strong> is marked merged and all
          its history (contact logs, assessments, donations, etc.) moves to the winner. Pick the
          surviving value for any field where the two records disagree.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label>Winner (survives)</label>
            <select value={winnerId} onChange={(e) => setWinnerId(parseInt(e.target.value, 10))}>
              {members.map((m) => (
                <option key={recordId(kind, m)} value={recordId(kind, m)}>
                  {recordLabel(kind, m)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Loser (merged away)</label>
            <select value={loserId} onChange={(e) => setLoserId(parseInt(e.target.value, 10))}>
              {members
                .filter((m) => recordId(kind, m) !== winnerId)
                .map((m) => (
                  <option key={recordId(kind, m)} value={recordId(kind, m)}>
                    {recordLabel(kind, m)}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {isManualMerge && (
          <div
            className="alert"
            style={{
              background: '#fff8e6',
              border: '1px solid #FAA31B',
              marginTop: '1rem',
            }}
          >
            <strong>Manual merge.</strong> The duplicate scanner did not flag this pair. Make sure
            you have verified that these two records refer to the same{' '}
            {kind === 'client' ? 'person' : 'donor'} before continuing.
          </div>
        )}

        {namesLookDifferent && (
          <div
            className="alert alert-error"
            style={{ marginTop: '0.5rem' }}
            role="alert"
          >
            <strong>
              {kind === 'client' ? 'Last names' : 'Full names'} do not match.
            </strong>{' '}
            Double-check that this is not a different{' '}
            {kind === 'client' ? 'person' : 'donor'} before merging.
          </div>
        )}

        <h3 style={{ marginTop: '1.5rem' }}>Field choices</h3>
        {conflictingFields.length === 0 ? (
          <p style={{ color: '#666' }}>
            No conflicting fields — winner&apos;s data will be kept as-is.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Winner</th>
                <th>Loser</th>
                <th>Use</th>
              </tr>
            </thead>
            <tbody>
              {conflictingFields.map((f) => {
                const choice = choices[f.key] || 'winner';
                const w = (winner as unknown as Record<string, unknown>)[f.key];
                const l = (loser as unknown as Record<string, unknown>)[f.key];
                return (
                  <tr key={f.key}>
                    <td>{f.label}</td>
                    <td style={{ background: choice === 'winner' ? '#f4ecff' : undefined }}>
                      {fmt(w, f.format)}
                    </td>
                    <td style={{ background: choice === 'loser' ? '#fff4e0' : undefined }}>
                      {fmt(l, f.format)}
                    </td>
                    <td>
                      <label style={{ marginRight: '1rem' }}>
                        <input
                          type="radio"
                          name={`choice-${f.key}`}
                          checked={choice === 'winner'}
                          onChange={() =>
                            setChoices((prev) => ({ ...prev, [f.key]: 'winner' }))
                          }
                        />{' '}
                        Winner
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`choice-${f.key}`}
                          checked={choice === 'loser'}
                          onChange={() =>
                            setChoices((prev) => ({ ...prev, [f.key]: 'loser' }))
                          }
                        />{' '}
                        Loser
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="alert" style={{ background: '#fff8e6', marginTop: '1rem' }}>
          <strong>This action is not auto-reversible.</strong> The loser record will be marked
          merged, all child rows (contact logs, assessments, donations, family memberships, alerts)
          will be re-pointed to the winner, and the operation will be written to the audit log.
        </div>

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>
            Type <code>{expectedConfirm}</code> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={expectedConfirm}
            autoFocus
          />
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
          <button className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="button-primary"
            onClick={submit}
            disabled={submitting || confirmText.trim() !== expectedConfirm || winnerId === loserId}
          >
            {submitting ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
